import React from 'react';
import PropTypes from 'prop-types'; // Optional, but recommended for prop validation
import { Info } from 'lucide-react'; // Assuming Info icon is always used

// Helper to generate dynamic Tailwind classes, keeping dark mode in mind
// Note: Ensure Tailwind's JIT engine can find these classes. You might need to safelist patterns
// in tailwind.config.js if you encounter issues with dynamically generated classes.
const getActiveClasses = (colorName) => ({
  bg: `bg-${colorName}-50 dark:bg-${colorName}-900/30`,
  border: `border-${colorName}-100 dark:border-${colorName}-800/50`,
  toggle: `peer-checked:bg-${colorName}-500 dark:peer-checked:bg-${colorName}-600`,
  icon: `text-${colorName}-500 dark:text-${colorName}-400`,
});

const defaultClasses = {
  bg: 'bg-white dark:bg-gray-750',
  border: '',
  toggle: 'bg-gray-300 dark:bg-gray-600', // Base toggle color
};

const PolicyCard = ({
  policyKey,
  title,
  description,
  icon: IconComponent, // Receive the icon component as a prop
  isActive,
  onToggle,
  onShowDetails,
  renderImpact,
  activeColor = 'blue', // Default active color if not specified
}) => {
  const currentClasses = isActive ? getActiveClasses(activeColor) : defaultClasses;
  const iconClasses = isActive ? currentClasses.icon : 'text-black dark:text-gray-400'; // Icon color changes based on state

  return (
    <div className={`rounded-lg transition-all border ${currentClasses.bg} ${currentClasses.border}`}>
      {/* Card Header: Title + Toggle */}
      <div className="grid grid-cols-[1fr,auto] items-center gap-2 p-3 sm:p-4">
        <span className="flex items-center font-medium text-sm sm:text-base text-black">
          {/* Render the passed icon component */}
          {IconComponent && <IconComponent className={`w-4 h-4 mr-2 ${iconClasses} flex-shrink-0`} />}
          <span className="truncate">{title}</span>
        </span>
        <div className="flex items-center justify-self-end">
          <button
            type="button"
            onClick={() => onShowDetails(policyKey)}
            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-600 mr-2 focus:outline-none focus:ring-1 focus:ring-gray-400"
            aria-label={`${title} Policy Information`}
          >
            <Info className="w-4 h-4 text-gray-400 dark:text-gray-500" />
          </button>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={isActive}
              onChange={() => onToggle(policyKey, !isActive)}
              className="sr-only peer"
              aria-label={`Toggle ${title} Policy`}
            />
            <div className={`w-10 h-6 ${defaultClasses.toggle} peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-offset-2 dark:peer-focus:ring-offset-gray-800 peer-focus:ring-${activeColor}-300 dark:peer-focus:ring-${activeColor}-800 rounded-full peer ${currentClasses.toggle} peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-gray-300 dark:after:border-gray-500 after:rounded-full after:h-5 after:w-5 after:transition-all`}></div>
          </label>
        </div>
      </div>
      {/* Card Body: Description + Impact */}
      <div className="px-3 pb-3 sm:px-4 sm:pb-4">
        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
          {description}
        </p>
        <div className="overflow-x-auto">
          {/* Render impact details using the passed function */}
          {renderImpact(policyKey)}
        </div>
      </div>
    </div>
  );
};

// Optional: Add PropTypes for better development experience
PolicyCard.propTypes = {
  policyKey: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired, // Expect a component type
  isActive: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onShowDetails: PropTypes.func.isRequired,
  renderImpact: PropTypes.func.isRequired,
  activeColor: PropTypes.string,
};

export default PolicyCard; // Export the component